import { Thermostat } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverList from 'components/KNDetailsPopover/KNDetailsPopoverList'
import type { KNDetailsPopoverListItem } from 'components/KNDetailsPopover/types'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { KNDangerousGoodsIcon } from 'components/KNIcon/KNMaterialIcon'
import { GuestUserContext } from 'context/authentication/GuestUserContext'
import {
  getFluidsVolumeMeasurement,
  getFluidsVolumeUnit,
  getFormattedUnit,
  getTemperatureMeasurement,
  getTemperatureUnit,
  getWeightMeasurement,
  getWeightUnit,
} from 'global/helpers/units'
import i18n from 'i18n'
import { type ReactElement, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import type { DangerousGoods, QuoteRequest } from 'screens/QuoteDetails/QuoteDetails.types'
import type { MeasurementsSystem } from 'screens/UserManager/UserManager.types'

interface QuoteHeaderProps {
  quoteRequest: QuoteRequest
}

const hasTemperatureRequirements = (quoteRequest: QuoteRequest) =>
  quoteRequest.requirements?.temperature?.minimum !== null && quoteRequest.requirements?.temperature?.maximum !== null

const hasDangerousGoods = (quoteRequest: QuoteRequest) =>
  quoteRequest.shipments.some(shipment => shipment.cargoItems.some(cargo => cargo.dangerousGoods.length > 0))

const getDangerousGoodsItems = (dangerousGoods: DangerousGoods, measurementsSystem: MeasurementsSystem): KNDetailsPopoverListItem[] => {
  const items: KNDetailsPopoverListItem[] = []
  if (dangerousGoods.limitedQuantity) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.limitedQuantity'),
      value: dangerousGoods.limitedQuantity ? i18n.t('general.yes') : i18n.t('general.no'),
    })
  }
  if (dangerousGoods.exceptedQuantity) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.exceptedQuantity'),
      value: dangerousGoods.exceptedQuantity ? i18n.t('general.yes') : i18n.t('general.no'),
    })
  }
  if (dangerousGoods.name) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.name'),
      value: dangerousGoods.name,
      stacked: true,
    })
  }
  if (dangerousGoods.technicalName) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.technicalName'),
      value: dangerousGoods.technicalName,
      stacked: true,
    })
  }
  if (dangerousGoods.englishTechnicalName) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.englishTechnicalName'),
      value: dangerousGoods.englishTechnicalName,
      stacked: true,
    })
  }
  if (dangerousGoods.weight) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.weight'),
      value: `${getWeightMeasurement(dangerousGoods.weight, measurementsSystem)} ${getFormattedUnit(getWeightUnit(measurementsSystem))}`,
    })
  }
  if (dangerousGoods.volume) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.volume'),
      value: `${getFluidsVolumeMeasurement(dangerousGoods.volume, measurementsSystem)} ${getFormattedUnit(getFluidsVolumeUnit(measurementsSystem))}`,
    })
  }
  if (dangerousGoods.classification) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.class'),
      value: dangerousGoods.classification,
    })
  }
  if (dangerousGoods.netExplosiveMass) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.netExplosiveMass'),
      value: dangerousGoods.netExplosiveMass,
    })
  }
  if (dangerousGoods.highConsequence) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.highConsequence'),
      value: dangerousGoods.highConsequence ? i18n.t('general.yes') : i18n.t('general.no'),
    })
  }
  if (dangerousGoods.environmentalHazard) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.environmentalHazard'),
      value: dangerousGoods.environmentalHazard ? i18n.t('general.yes') : i18n.t('general.no'),
    })
  }
  if (dangerousGoods.packingGroup) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.packingGroup'),
      value: dangerousGoods.packingGroup,
    })
  }
  if (dangerousGoods.tunnelCode) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.tunnelCode'),
      value: dangerousGoods.tunnelCode,
    })
  }
  if (dangerousGoods.adrExemptions) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.adrExemptions'),
      value: dangerousGoods.adrExemptions,
    })
  }
  if (dangerousGoods.adrPackage?.type) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.adrPackageType'),
      value: dangerousGoods.adrPackage.type,
    })
  }
  if (dangerousGoods.adrPackage?.number) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.adrPackageNumber'),
      value: dangerousGoods.adrPackage.number,
    })
  }
  if (dangerousGoods.notOtherwiseSpecified) {
    items.push({
      label: i18n.t('screens.cs.carrier_quotes.quote_accordion.notOtherwiseSpecified'),
      value: dangerousGoods.notOtherwiseSpecified ? i18n.t('general.yes') : i18n.t('general.no'),
    })
  }
  return items
}

const QuoteHeader = ({
  quoteRequest,
}: QuoteHeaderProps): ReactElement => {
  const { t } = useTranslation()
  const [guestUserState, _guestUserDispatch] = useContext(GuestUserContext)

  const {
    anchorEl: temperatureRequirementsAnchorEl,
    open: temperatureRequirementsOpen,
    handleClick: handleTemperatureRequirementsClick,
    handleClose: handleTemperatureRequirementsClose,
  } = usePopoverState()

  const {
    anchorEl: dangerousGoodsAnchorEl,
    open: dangerousGoodsOpen,
    handleClick: handleDangerousGoodsClick,
    handleClose: handleDangerousGoodsClose,
  } = usePopoverState()

  const showTemperatureRequirements = hasTemperatureRequirements(quoteRequest)
  const showDangerousGoods = hasDangerousGoods(quoteRequest)

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" color="primary.main" className="hover-link">
          {quoteRequest.quoteReference}
        </Typography>
        <Stack direction="row" spacing={1}>
          {showTemperatureRequirements && (
            <IconButton
              onClick={handleTemperatureRequirementsClick}
              size="small"
              color="primary"
              sx={{ padding: 0 }}
            >
              <Thermostat />
            </IconButton>
          )}
          {showDangerousGoods && (
            <IconButton
              onClick={handleDangerousGoodsClick}
              size="small"
              color="primary"
              sx={{ padding: 0 }}
            >
              <KNDangerousGoodsIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {showTemperatureRequirements && (
        <KNDetailsPopover
          open={temperatureRequirementsOpen}
          onClose={handleTemperatureRequirementsClose}
          anchorEl={temperatureRequirementsAnchorEl}
        >
          <KNDetailsPopoverList
            description={t('screens.cs.carrier_quotes.equipment.temperature')}
            items={[
              {
                value: t('screens.cs.carrier_quotes.equipment.range', {
                  rangeMin: getTemperatureMeasurement(quoteRequest.requirements!.temperature!.minimum!, guestUserState.measurementsSystem), // NOSONAR
                  rangeMax: getTemperatureMeasurement(quoteRequest.requirements!.temperature!.maximum!, guestUserState.measurementsSystem), // NOSONAR
                  unit: getFormattedUnit(getTemperatureUnit(guestUserState.measurementsSystem)),
                }),
              },
            ]}
          />
        </KNDetailsPopover>
      )}

      {showDangerousGoods && (
        <KNDetailsPopover
          open={dangerousGoodsOpen}
          onClose={handleDangerousGoodsClose}
          anchorEl={dangerousGoodsAnchorEl}
        >
          {quoteRequest.shipments.map((shipment) => shipment.cargoItems.map((cargo) => cargo.dangerousGoods.map((dangerousGoods) => (
            <KNDetailsPopoverList
              key={dangerousGoods.unCode}
              header={cargo.description}
              description={dangerousGoods.unCode}
              items={getDangerousGoodsItems(dangerousGoods, guestUserState.measurementsSystem)}
            />
          ))))}
        </KNDetailsPopover>
      )}
    </>
  )
}

export default QuoteHeader
